import { useForm } from '@inertiajs/react'
import DashboardLayout from '@/components/DashboardLayout'
import { useAuth } from '@/components/Layout'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Calendar } from '@/components/ui/calendar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '@/components/ui/command'
import {
  CalendarIcon,
  Clock,
  Pencil,
  Check,
  ChevronsUpDown,
  Plus,
  X,
  Users,
  Waves,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { boardTypeLabel } from '@/lib/equipment'
import type { BoardType } from '@/types/equipment'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useState, useEffect } from 'react'

interface Teacher {
  id: number
  name: string
  hourly_rate?: number
}

interface Student {
  id: number
  name: string
}

interface StudentSubscription {
  id: number
  lesson_package_id: number
  lesson_package_name: string
  lessons_remaining: number
  expires_at: string | null
}

type BoardSource = 'own' | 'school' | ''

interface EquipmentOption {
  id: number
  board_type: string
  size: string | null
  nickname: string | null
  available: number | null
}

interface Lesson {
  id: number
  lesson_date: string
  start_time: string
  end_time: string
  teacher_id: number | null
  student_id: number | null
  student_subscription_id: number | null
  teacher_payment: string
  description: string
  board_source: BoardSource | null
  equipment_id: number | null
  extra_students: {
    student_id: number
    student_subscription_id: number | null
    board_source: BoardSource | null
    equipment_id: number | null
  }[]
  total_vagas: number
  occupied_vagas: number
}

interface ExtraStudent {
  student_id: string
  student_subscription_id: string
  board_source: BoardSource
  equipment_id: string
}

interface Props {
  lesson: Lesson
  teachers: Teacher[]
  students: Student[]
  student_subscriptions: Record<string, StudentSubscription[]>
  available_equipments: EquipmentOption[]
  equipments_index: EquipmentOption[]
}

export default function Edit({
  lesson,
  teachers,
  students,
  student_subscriptions,
  available_equipments,
  equipments_index,
}: Props) {
  const { user } = useAuth()
  const isOwner = user?.role === 'owner'
  const [date, setDate] = useState<Date>(
    new Date(lesson.lesson_date + 'T00:00:00'),
  )
  const [selectedStudentSubs, setSelectedStudentSubs] = useState<
    StudentSubscription[]
  >([])
  const [studentComboboxOpen, setStudentComboboxOpen] = useState(false)
  const [extraComboboxOpen, setExtraComboboxOpen] = useState(false)
  const TEACHER_NONE = '__none__'
  const { data, setData, patch, processing, errors, transform, clearErrors } =
    useForm({
      lesson_date: lesson.lesson_date,
      start_time: lesson.start_time,
      end_time: lesson.end_time,
      teacher_id: lesson.teacher_id?.toString() || TEACHER_NONE,
      student_id: lesson.student_id?.toString() || '',
      student_subscription_id: lesson.student_subscription_id?.toString() || '',
      teacher_payment: lesson.teacher_payment,
      description: lesson.description || '',
      board_source: (lesson.board_source || '') as BoardSource,
      equipment_id: lesson.equipment_id?.toString() || '',
      extra_students: (lesson.extra_students || []).map((e) => ({
        student_id: e.student_id.toString(),
        student_subscription_id: e.student_subscription_id?.toString() || '',
        board_source: (e.board_source || '') as BoardSource,
        equipment_id: e.equipment_id?.toString() || '',
      })) as ExtraStudent[],
      total_vagas: (lesson.total_vagas ?? 1).toString(),
    })

  const studentName = (id: string) =>
    students.find((s) => s.id.toString() === id)?.name || ''

  const addExtraStudent = (id: string) => {
    if (data.extra_students.some((e) => e.student_id === id)) return
    const subs = student_subscriptions[id] || []
    const firstSub = subs.find((s) => s.lessons_remaining > 0)
    setData('extra_students', [
      ...data.extra_students,
      {
        student_id: id,
        student_subscription_id: firstSub ? firstSub.id.toString() : '',
        board_source: '',
        equipment_id: '',
      },
    ])
  }

  const removeExtraStudent = (id: string) => {
    setData(
      'extra_students',
      data.extra_students.filter((e) => e.student_id !== id),
    )
  }

  const setExtraSub = (id: string, subId: string) => {
    setData(
      'extra_students',
      data.extra_students.map((e) =>
        e.student_id === id ? { ...e, student_subscription_id: subId } : e,
      ),
    )
  }

  const setExtraBoard = (
    id: string,
    board_source: BoardSource,
    equipment_id: string,
  ) => {
    setData(
      'extra_students',
      data.extra_students.map((e) =>
        e.student_id === id ? { ...e, board_source, equipment_id } : e,
      ),
    )
  }

  // Alunos disponíveis para adicionar como extra (exclui o principal e já adicionados)
  const availableExtraStudents = students.filter(
    (s) =>
      s.id.toString() !== data.student_id &&
      !data.extra_students.some((e) => e.student_id === s.id.toString()),
  )

  // Inicializa as assinaturas do aluno selecionado
  useEffect(() => {
    if (data.student_id) {
      const subs = student_subscriptions[data.student_id] || []
      setSelectedStudentSubs(subs)
    }
  }, [])

  // Atualiza as assinaturas disponíveis quando o aluno muda
  useEffect(() => {
    if (data.student_id) {
      const subs = student_subscriptions[data.student_id] || []
      setSelectedStudentSubs(subs)

      // Auto-seleciona a primeira assinatura com aulas disponíveis
      const availableSub = subs.find((s) => s.lessons_remaining > 0)
      if (availableSub) {
        setData('student_subscription_id', availableSub.id.toString())
      } else {
        setData('student_subscription_id', '')
      }
    } else {
      setSelectedStudentSubs([])
      setData('student_subscription_id', '')
    }
  }, [data.student_id])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    transform((d) => ({
      ...d,
      teacher_id: d.teacher_id === TEACHER_NONE ? '' : d.teacher_id,
    }))
    patch(`/lessons/${lesson.id}`)
  }

  const handleForceSubmit = () => {
    clearErrors()
    transform((d) => ({
      ...d,
      teacher_id: d.teacher_id === TEACHER_NONE ? '' : d.teacher_id,
      force: '1',
    }))
    patch(`/lessons/${lesson.id}`)
  }

  const errBase = (errors as unknown as Record<string, string | undefined>).base
  const errForceOffer =
    (errors as unknown as Record<string, string | undefined>).force_offer ===
    '1'

  const handleDateSelect = (selectedDate: Date | undefined) => {
    if (selectedDate) {
      setDate(selectedDate)
      setData('lesson_date', format(selectedDate, 'yyyy-MM-dd'))
    }
  }

  const calculateDuration = () => {
    if (data.start_time && data.end_time) {
      const [startHour, startMin] = data.start_time.split(':').map(Number)
      const [endHour, endMin] = data.end_time.split(':').map(Number)

      const startMinutes = startHour * 60 + startMin
      const endMinutes = endHour * 60 + endMin

      const durationMinutes = endMinutes - startMinutes

      if (durationMinutes > 0) {
        const hours = Math.floor(durationMinutes / 60)
        const minutes = durationMinutes % 60
        return `${hours}h ${minutes}min`
      }
    }
    return null
  }

  const duration = calculateDuration()

  // ── Seletor de prancha (por aluno) ──
  const BOARD_NONE = '__none__'
  const BOARD_OWN = '__own__'

  const boardLabel = (eq: EquipmentOption) => {
    const base = `${boardTypeLabel(eq.board_type as BoardType)}${eq.size ? ' ' + eq.size : ''}`
    const nick = eq.nickname ? ` · ${eq.nickname}` : ''
    const avail = eq.available != null ? ` (${eq.available} disp.)` : ''
    return `${base}${nick}${avail}`
  }

  // Pranchas para o select: disponíveis no horário + a já selecionada (caso esteja
  // esgotada agora mas tenha sido atribuída antes), para o admin poder mantê-la.
  const boardOptions = (selectedId: string): EquipmentOption[] => {
    const list = [...available_equipments]
    if (selectedId && !list.some((e) => e.id.toString() === selectedId)) {
      const fromIndex = equipments_index.find(
        (e) => e.id.toString() === selectedId,
      )
      if (fromIndex) list.push(fromIndex)
    }
    return list
  }

  const renderBoardPicker = (
    source: BoardSource,
    equipmentId: string,
    onChange: (source: BoardSource, equipmentId: string) => void,
  ) => {
    const value =
      source === 'own'
        ? BOARD_OWN
        : source === 'school' && equipmentId
          ? `eq-${equipmentId}`
          : BOARD_NONE
    return (
      <Select
        value={value}
        onValueChange={(v) => {
          if (v === BOARD_NONE) onChange('', '')
          else if (v === BOARD_OWN) onChange('own', '')
          else onChange('school', v.replace('eq-', ''))
        }}
      >
        <SelectTrigger>
          <SelectValue placeholder="Prancha..." />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={BOARD_NONE}>Sem prancha definida</SelectItem>
          <SelectItem value={BOARD_OWN}>Prancha própria do aluno</SelectItem>
          {boardOptions(equipmentId).map((eq) => (
            <SelectItem key={eq.id} value={`eq-${eq.id}`}>
              {boardLabel(eq)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    )
  }

  return (
    <DashboardLayout>
      <div className="p-8 max-w-3xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Pencil className="w-5 h-5 text-primary" />
              </div>
              Editar Aula
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Data da Aula */}
              <div className="space-y-2">
                <Label htmlFor="lesson_date">Data da Aula *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {date
                        ? format(date, 'PPP', { locale: ptBR })
                        : 'Selecione uma data'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={handleDateSelect}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                {errors.lesson_date && (
                  <p className="text-sm text-destructive">
                    {errors.lesson_date}
                  </p>
                )}
              </div>

              {/* Horários */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="start_time">Horário de Início *</Label>
                  <Input
                    id="start_time"
                    type="time"
                    value={data.start_time}
                    onChange={(e) => setData('start_time', e.target.value)}
                    required
                  />
                  {errors.start_time && (
                    <p className="text-sm text-destructive">
                      {errors.start_time}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="end_time">Horário de Término *</Label>
                  <Input
                    id="end_time"
                    type="time"
                    value={data.end_time}
                    onChange={(e) => setData('end_time', e.target.value)}
                    required
                  />
                  {errors.end_time && (
                    <p className="text-sm text-destructive">
                      {errors.end_time}
                    </p>
                  )}
                </div>
              </div>

              {/* Duração calculada */}
              {duration && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted p-3 rounded-lg">
                  <Clock className="w-4 h-4" />
                  <span>
                    Duração: <strong>{duration}</strong>
                  </span>
                </div>
              )}

              {/* Professor */}
              <div className="space-y-2">
                <Label htmlFor="teacher_id">Professor</Label>
                <Select
                  value={data.teacher_id || TEACHER_NONE}
                  onValueChange={(value) => {
                    setData('teacher_id', value)
                    // Preenche o valor do pagamento com o hourly_rate do professor selecionado
                    if (value && value !== TEACHER_NONE) {
                      const teacher = teachers.find(
                        (t) => t.id.toString() === value,
                      )
                      if (
                        teacher?.hourly_rate &&
                        (!data.teacher_payment ||
                          data.teacher_payment === '0' ||
                          data.teacher_payment === '')
                      ) {
                        setData(
                          'teacher_payment',
                          teacher.hourly_rate.toString(),
                        )
                      }
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione um professor" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={TEACHER_NONE}>A definir</SelectItem>
                    {teachers.map((teacher) => (
                      <SelectItem
                        key={teacher.id}
                        value={teacher.id.toString()}
                      >
                        {teacher.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Aulas agendadas pelo app ficam sem professor até você associar
                  aqui.
                </p>
                {errors.teacher_id && (
                  <p className="text-sm text-destructive">
                    {errors.teacher_id}
                  </p>
                )}
              </div>

              {/* Aluno */}
              <div className="space-y-2">
                <Label htmlFor="student_id">Aluno *</Label>
                <Popover
                  open={studentComboboxOpen}
                  onOpenChange={setStudentComboboxOpen}
                >
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      role="combobox"
                      aria-expanded={studentComboboxOpen}
                      className={cn(
                        'border-input flex w-full items-center justify-between gap-2 rounded-md border bg-transparent px-3 py-2 text-sm shadow-xs h-9',
                        'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] outline-none',
                        !data.student_id && 'text-muted-foreground',
                      )}
                    >
                      <span className="truncate">
                        {data.student_id
                          ? students.find(
                              (s) => s.id.toString() === data.student_id,
                            )?.name
                          : 'Selecione um aluno'}
                      </span>
                      <ChevronsUpDown className="size-4 shrink-0 opacity-50" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="!w-[var(--radix-popover-trigger-width)] !p-0"
                    align="start"
                  >
                    <Command>
                      <CommandInput placeholder="Buscar aluno..." />
                      <CommandList>
                        <CommandEmpty>Nenhum aluno encontrado.</CommandEmpty>
                        <CommandGroup>
                          {students.map((student) => (
                            <CommandItem
                              key={student.id}
                              value={student.name}
                              onSelect={() => {
                                setData('student_id', student.id.toString())
                                setStudentComboboxOpen(false)
                              }}
                            >
                              <Check
                                className={cn(
                                  'mr-2 h-4 w-4',
                                  data.student_id === student.id.toString()
                                    ? 'opacity-100'
                                    : 'opacity-0',
                                )}
                              />
                              {student.name}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                {errors.student_id && (
                  <p className="text-sm text-destructive">
                    {errors.student_id}
                  </p>
                )}
              </div>

              {/* Pacote/Assinatura do Aluno */}
              {selectedStudentSubs.length > 0 && (
                <div className="space-y-2">
                  <Label htmlFor="student_subscription_id">
                    Pacote de Aulas *
                  </Label>
                  <Select
                    value={data.student_subscription_id}
                    onValueChange={(value) =>
                      setData('student_subscription_id', value)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o pacote" />
                    </SelectTrigger>
                    <SelectContent>
                      {selectedStudentSubs.map((sub) => (
                        <SelectItem
                          key={sub.id}
                          value={sub.id.toString()}
                          disabled={sub.lessons_remaining <= 0}
                        >
                          {sub.lesson_package_name} ({sub.lessons_remaining}{' '}
                          {sub.lessons_remaining === 1
                            ? 'aula restante'
                            : 'aulas restantes'}
                          )
                          {sub.expires_at &&
                            ` - válido até ${format(new Date(sub.expires_at), 'dd/MM/yyyy')}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.student_subscription_id && (
                    <p className="text-sm text-destructive">
                      {errors.student_subscription_id}
                    </p>
                  )}
                </div>
              )}

              {/* Prancha do aluno principal */}
              {data.student_id && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Waves className="w-4 h-4 text-primary" />
                    Prancha do aluno
                  </Label>
                  {renderBoardPicker(
                    data.board_source,
                    data.equipment_id,
                    (source, equipmentId) => {
                      setData('board_source', source)
                      setData('equipment_id', equipmentId)
                    },
                  )}
                  <p className="text-xs text-muted-foreground">
                    Escolha uma prancha do inventário da escola ou marque que o
                    aluno usa prancha própria. Só aparecem pranchas livres neste
                    horário.
                  </p>
                </div>
              )}

              {/* Outros alunos (mesmo professor, mesma aula) */}
              {data.student_id && (
                <div className="space-y-3 rounded-lg border p-4">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-primary" />
                    <Label className="m-0">Outros alunos nesta aula</Label>
                  </div>
                  <p className="text-xs text-muted-foreground -mt-1">
                    O professor dará esta aula para mais de um aluno. Cada aluno
                    extra desconta 1 aula do próprio pacote.
                  </p>

                  {data.extra_students.map((extra) => {
                    const subs = student_subscriptions[extra.student_id] || []
                    return (
                      <div
                        key={extra.student_id}
                        className="flex flex-col gap-2 rounded-md bg-muted/50 p-3 sm:flex-row sm:items-center"
                      >
                        <span className="flex-1 text-sm font-medium">
                          {studentName(extra.student_id)}
                        </span>
                        {subs.length > 0 ? (
                          <Select
                            value={extra.student_subscription_id}
                            onValueChange={(value) =>
                              setExtraSub(extra.student_id, value)
                            }
                          >
                            <SelectTrigger className="sm:w-72">
                              <SelectValue placeholder="Selecione o pacote" />
                            </SelectTrigger>
                            <SelectContent>
                              {subs.map((sub) => (
                                <SelectItem
                                  key={sub.id}
                                  value={sub.id.toString()}
                                  disabled={sub.lessons_remaining <= 0}
                                >
                                  {sub.lesson_package_name} (
                                  {sub.lessons_remaining}{' '}
                                  {sub.lessons_remaining === 1
                                    ? 'aula restante'
                                    : 'aulas restantes'}
                                  )
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className="text-xs text-muted-foreground sm:w-72">
                            Sem pacote ativo (sem débito)
                          </span>
                        )}
                        <div className="sm:w-60">
                          {renderBoardPicker(
                            extra.board_source,
                            extra.equipment_id,
                            (source, equipmentId) =>
                              setExtraBoard(
                                extra.student_id,
                                source,
                                equipmentId,
                              ),
                          )}
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeExtraStudent(extra.student_id)}
                          aria-label="Remover aluno"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    )
                  })}

                  <Popover
                    open={extraComboboxOpen}
                    onOpenChange={setExtraComboboxOpen}
                  >
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="w-full justify-start"
                      >
                        <Plus className="mr-2 h-4 w-4" />
                        Adicionar aluno
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="!w-[var(--radix-popover-trigger-width)] !p-0"
                      align="start"
                    >
                      <Command>
                        <CommandInput placeholder="Buscar aluno..." />
                        <CommandList>
                          <CommandEmpty>Nenhum aluno disponível.</CommandEmpty>
                          <CommandGroup>
                            {availableExtraStudents.map((student) => (
                              <CommandItem
                                key={student.id}
                                value={student.name}
                                onSelect={() => {
                                  addExtraStudent(student.id.toString())
                                  setExtraComboboxOpen(false)
                                }}
                              >
                                {student.name}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
              )}

              {/* Quantidade de Vagas (Lessons paralelas) */}
              <div className="space-y-2 rounded-lg border bg-muted/30 p-3">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <Label htmlFor="total_vagas" className="font-semibold">
                      Vagas neste horário
                    </Label>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {lesson.occupied_vagas} de {lesson.total_vagas} ocupadas.
                      Aumentar cria vagas vazias; diminuir só remove as livres.
                    </p>
                  </div>
                  <Input
                    id="total_vagas"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    className="w-24 text-center"
                    value={data.total_vagas}
                    onFocus={(e) => e.target.select()}
                    onChange={(e) => {
                      const raw = e.target.value.replace(/\D/g, '')
                      setData('total_vagas', raw)
                    }}
                  />
                </div>
              </div>

              {/* Valor do Professor — só o dono. Ver Lessons/New.tsx. */}
              {isOwner && (
                <div className="space-y-2">
                  <Label htmlFor="teacher_payment">
                    Valor a Pagar ao Professor
                  </Label>
                  <Input
                    id="teacher_payment"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={data.teacher_payment}
                    onChange={(e) => setData('teacher_payment', e.target.value)}
                  />
                  {errors.teacher_payment && (
                    <p className="text-sm text-destructive">
                      {errors.teacher_payment}
                    </p>
                  )}
                </div>
              )}

              {/* Observações */}
              <div className="space-y-2">
                <Label htmlFor="description">Observações</Label>
                <Textarea
                  id="description"
                  placeholder="Observações sobre a aula..."
                  value={data.description}
                  onChange={(e) => setData('description', e.target.value)}
                  rows={3}
                />
                {errors.description && (
                  <p className="text-sm text-destructive">
                    {errors.description}
                  </p>
                )}
              </div>

              {/* Botões */}
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => window.history.back()}
                  className="flex-1"
                >
                  Cancelar
                </Button>
                <Button type="submit" disabled={processing} className="flex-1">
                  {processing ? 'Salvando...' : 'Salvar Alterações'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Dialog: pacote vencido/sem aulas — admin pode forcar */}
      <AlertDialog open={!!errBase && errForceOffer}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Agendar mesmo sem pacote ativo?</AlertDialogTitle>
            <AlertDialogDescription>
              {errBase} A aula será criada e o pacote mais recente do aluno será
              debitado mesmo que vencido ou zerado (saldo pode ficar negativo).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => clearErrors()}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleForceSubmit}>
              Agendar mesmo assim
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  )
}
